<script lang="ts">
// FIX: Simplify component.
/* eslint-disable vue/max-lines-per-block */

// Content is directly controlled by developers.
/* eslint-disable vue/no-v-html */
</script>

<script setup lang="ts">
import type { ServicesRelaxCarouselSlide } from '.';
const props = defineProps<{ item: ServicesRelaxCarouselSlide }>();
const expanded = ref(false),
  isTruncated = ref(true);
const availableHeight = ref<number | null>(null);
type RT = HTMLElement | { $el: HTMLElement };
const refs = {
  p: useTemplateRef<RT>('paragraphRef'),
  t: useTemplateRef<RT>('titleRef'),
  s: useTemplateRef<RT>('slideRef'),
  a: useTemplateRef<RT>('articleRef'),
};
let ro: ResizeObserver | null = null;
const el = (r: RT | null) =>
  r ? ((r as { $el?: HTMLElement }).$el ?? (r as HTMLElement)) : null;

const update = () => {
  void nextTick(() => {
    const s = el(refs.s.value),
      t = el(refs.t.value),
      p = el(refs.p.value),
      a = el(refs.a.value);
    if (!s || !t || !p || !a) {
      return;
    }
    const tR = t.getBoundingClientRect(),
      pR = p.getBoundingClientRect(),
      gap =
        Math.max(0, pR.top - tR.bottom) ||
        parseFloat(getComputedStyle(p).marginTop || '0'),
      aS = getComputedStyle(a),
      pads =
        (parseFloat(aS.paddingTop) || 0) + (parseFloat(aS.paddingBottom) || 0);
    availableHeight.value = Math.max(
      0,
      Math.floor(
        s.getBoundingClientRect().height - tR.height - gap - pads - 24,
      ) - 1,
    );
    const wasClamped = p.classList.contains('clamped');
    if (wasClamped) {
      p.classList.remove('clamped');
    }
    isTruncated.value = p.scrollHeight > availableHeight.value;
    if (wasClamped) {
      p.classList.add('clamped');
    }
    if (!isTruncated.value) {
      expanded.value = false;
    }
  }).catch(console.warn);
};

const toggleExpanded = () => {
  expanded.value = !expanded.value;
  update();
  if (!expanded.value) {
    return;
  }
  void nextTick(() => {
    const p = el(refs.p.value);
    if (!p) {
      return;
    }
    p.scrollTop = 0;
    p.focus();
  }).catch(console.warn);
};

onNuxtReady(() => {
  update();
  if (typeof ResizeObserver === 'undefined') {
    return;
  }
  ro = new ResizeObserver(update);
  Object.values(refs).forEach((r) => {
    const node = el(r.value);
    if (node) {
      ro?.observe(node);
    }
  });
});
onMounted(() => {
  window.addEventListener('resize', update);
});
onBeforeUnmount(() => {
  ro?.disconnect();
  window.removeEventListener('resize', update);
});
watch(() => props.item.description, update);
</script>

<template>
  <SwiperSlide>
    <AppSwiperSlideBase
      ref="slideRef"
      class="relative aspect-square w-full sm:aspect-917/480"
    >
      <img
        :src="props.item.image.url"
        :alt="props.item.image.alt"
        class="size-full object-cover object-[50%_60%]"
      />
      <div
        class="from-client-black/70 via-client-black/40 to-client-black/80 absolute inset-0 size-full bg-linear-to-b"
      ></div>

      <article
        ref="articleRef"
        class="p-r6 lg:p-r12 gap-r4 text-client-white absolute bottom-0 left-0 flex flex-col"
      >
        <AppHeading ref="titleRef" tag="h3">
          {{ props.item.title }}
        </AppHeading>

        <div class="relative">
          <AppParagraph
            ref="paragraphRef"
            :class="expanded ? 'desc-scroll' : isTruncated ? 'clamped' : ''"
            :aria-expanded="expanded"
            tag="div"
            role="region"
            size="custom"
            :style="
              expanded && availableHeight !== null
                ? { maxHeight: `${availableHeight}px` }
                : undefined
            "
            tabindex="-1"
            class="text-base 2xl:text-lg"
          >
            <div v-html="props.item.description"></div>
          </AppParagraph>

          <button
            v-if="isTruncated || expanded"
            type="button"
            class="toggle-btn mt-2 text-sm underline"
            :aria-expanded="expanded"
            @click="toggleExpanded"
          >
            {{ expanded ? 'Zobrazit méně' : 'Zobrazit více' }}
          </button>
        </div>
      </article>
    </AppSwiperSlideBase>
  </SwiperSlide>
</template>

<style scoped>
.clamped {
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-word;
}

.desc-scroll {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.toggle-btn {
  background: transparent;
  border: none;
  color: inherit;
  padding: 0;
  cursor: pointer;
  position: relative;
  z-index: 2;
}

.clamped,
.desc-scroll {
  position: relative;
  z-index: 1;
}
</style>
